import { useMemo } from 'react';

const DEVICE_ID_KEY = 'pix_jsr_device_id';

interface OsPattern {
  readonly regex: RegExp;
  readonly name: string;
}

const OS_PATTERNS: readonly OsPattern[] = [
  { regex: /Windows NT ([\d.]+)/, name: 'Windows' },
  { regex: /Mac OS X ([\d_.]+)/, name: 'macOS' },
  { regex: /Android ([\d.]+)/, name: 'Android' },
  { regex: /iPhone OS ([\d_]+)/, name: 'iOS' },
  { regex: /Linux/, name: 'Linux' },
];

function getOrCreateDeviceId(): string {
  const stored = typeof localStorage !== 'undefined' && localStorage.getItem(DEVICE_ID_KEY);
  if (stored) {
    return stored;
  }
  const deviceId = window.crypto.randomUUID();
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
}

function parseOsVersion(): string {
  const ua = navigator.userAgent;

  for (const { regex, name } of OS_PATTERNS) {
    const match = ua.match(regex);
    if (match) {
      const version = match[1] ? match[1].replace(/_/g, '.') : '';
      return version ? `${name} ${version}` : name;
    }
  }

  return 'Unknown';
}

function formatTimezoneOffset(): string {
  const offsetMinutes = new Date().getTimezoneOffset();
  const sign = offsetMinutes <= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
  const minutes = String(absMinutes % 60).padStart(2, '0');
  return `${sign}${hours}:${minutes}`;
}

export interface RiskSignals {
  readonly device_id: string;
  readonly screenDimensions: { readonly height: number; readonly width: number };
  readonly language: string;
  readonly userTimeZoneOffset: string;
  readonly osVersion: string;
  readonly elapsedTimeSinceBoot: number;
  readonly isDevModeEnabled: boolean;
}

export function collectRiskSignals(): RiskSignals {
  return {
    device_id: getOrCreateDeviceId(),
    screenDimensions: {
      height: window.screen.height,
      width: window.screen.width,
    },
    language: navigator.language.split('-')[0],
    userTimeZoneOffset: formatTimezoneOffset(),
    osVersion: parseOsVersion(),
    elapsedTimeSinceBoot: Math.round(performance.now()),
    isDevModeEnabled: false,
  };
}

export function useRiskSignals(): () => RiskSignals {
  return useMemo(() => collectRiskSignals, []);
}
